import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { masterDashboard } from '../services/masterApi';
import LoadingSpinner from '../components/LoadingSpinner';

export default function MasterDashboard() {
  const [masterId] = useState(() => {
    const token = localStorage.getItem('masterToken');
    if (token) {
      try {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        return decoded.id;
      } catch {
        return null;
      }
    }
    return null;
  });

  const { data: summary, isLoading: summaryLoading } = useQuery(
    ['masterSummary', masterId],
    () => masterDashboard.getSummary(masterId),
    { enabled: !!masterId, retry: 1 }
  );

  const { data: stats, isLoading: statsLoading } = useQuery(
    ['masterStats', masterId],
    () => masterDashboard.getStats(masterId),
    { enabled: !!masterId, retry: 1 }
  );

  const { data: activities, isLoading: activitiesLoading } = useQuery(
    ['masterActivities', masterId],
    () => masterDashboard.getRecentActivities(masterId, 10),
    { enabled: !!masterId, retry: 1 }
  );

  if (!masterId) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          Unable to load dashboard. Please login again.
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900">Master Dashboard</h1>
          <p className="text-gray-600 mt-2">Manage your invoicing system and users</p>
        </div>

        {/* Stats Cards */}
        {summaryLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              title="Total Users"
              value={summary?.data?.totalUsers || 0}
              icon="👥"
              color="blue"
            />
            <StatCard
              title="Total Invoices"
              value={summary?.data?.totalInvoices || 0}
              icon="📄"
              color="green"
            />
            <StatCard
              title="Revenue"
              value={`Rs. ${(summary?.data?.totalRevenue || 0).toLocaleString()}`}
              icon="💰"
              color="purple"
            />
            <StatCard
              title="Pending Invoices"
              value={summary?.data?.pendingInvoices || 0}
              icon="⏳"
              color="orange"
            />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ActionButton
                  label="View Invoices"
                  href="/master/invoices"
                  icon="📋"
                />
                <ActionButton
                  label="Create Invoice"
                  href="/master/scenarios"
                  icon="✏️"
                />
                <ActionButton
                  label="Manage Users"
                  href="/master/users"
                  icon="👤"
                />
                <ActionButton
                  label="View Profile"
                  href="/master/profile"
                  icon="⚙️"
                />
              </div>
            </div>

            {/* Recent Activities */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Recent Activities</h2>
              {activitiesLoading ? (
                <LoadingSpinner />
              ) : activities?.data?.activities?.length > 0 ? (
                <div className="space-y-4">
                  {activities.data.activities.map((activity, idx) => (
                    <ActivityItem key={idx} activity={activity} />
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No recent activities</p>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Performance Summary */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Performance</h3>
              {statsLoading ? (
                <LoadingSpinner />
              ) : (
                <div className="space-y-3">
                  <StatRow label="Success Rate" value={`${stats?.data?.successRate || 0}%`} />
                  <StatRow label="Avg Processing Time" value={`${stats?.data?.avgTime || 0}s`} />
                  <StatRow label="Active Users" value={stats?.data?.activeUsers || 0} />
                </div>
              )}
            </div>

            {/* Support Widget */}
            <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
              <h3 className="text-lg font-bold text-blue-900 mb-2">Need Help?</h3>
              <p className="text-sm text-blue-800 mb-4">
                Contact our support team for assistance with your invoicing system.
              </p>
              <button className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors">
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, color }) {
  const colorClasses = {
    blue: 'from-blue-50 to-blue-100 border-blue-200',
    green: 'from-green-50 to-green-100 border-green-200',
    purple: 'from-purple-50 to-purple-100 border-purple-200',
    orange: 'from-orange-50 to-orange-100 border-orange-200',
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} border rounded-lg p-6`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm font-medium">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
        </div>
        <div className="text-4xl">{icon}</div>
      </div>
    </div>
  );
}

function ActionButton({ label, href, icon }) {
  return (
    <a
      href={href}
      className="flex items-center justify-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors group"
    >
      <span className="text-2xl mr-2">{icon}</span>
      <span className="font-semibold text-gray-700 group-hover:text-gray-900">{label}</span>
    </a>
  );
}

function StatRow({ label, value }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-gray-600 text-sm">{label}</span>
      <span className="font-semibold text-gray-900">{value}</span>
    </div>
  );
}

function ActivityItem({ activity }) {
  const getActivityColor = (type) => {
    const colors = {
      invoice_created: 'bg-green-100 text-green-800',
      invoice_submitted: 'bg-blue-100 text-blue-800',
      user_login: 'bg-gray-100 text-gray-800',
      user_created: 'bg-purple-100 text-purple-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0">
      <div className="flex-1">
        <p className="font-medium text-gray-900">{activity.description}</p>
        <p className="text-sm text-gray-500">{activity.user}</p>
      </div>
      <div className="flex items-center gap-3">
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getActivityColor(activity.type)}`}>
          {activity.type}
        </span>
        <span className="text-sm text-gray-500">{new Date(activity.timestamp).toLocaleDateString()}</span>
      </div>
    </div>
  );
}
